/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
  Container,
  IconButton,
} from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import MagediaImage3X from "../../../assets/Magedia3X.png";
import AhcImage3X from "../../../assets/AHC3XImage.png";
import AglinRefyer from "../../../assets/aglin-refyer.png";

import { textStyle } from "../../../shared/styles";
import { useTranslation } from "react-i18next";
import { useLanguageTransition } from "../../../LanguageTransitionContext";
import { Key, useEffect, useRef, useState } from "react";
import axios from "axios";
import { baseCMSURL } from "../../../shared/constants";

const PartnerLogosList = [MagediaImage3X, AglinRefyer, AhcImage3X];

const PartnerSectionComponent = ({ id }: any) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isRtl = i18n.language === "ar";
  const { isTransitioning } = useLanguageTransition();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [partnerLogos, setPartnerLogos] = useState<any>([]);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [showArrows, setShowArrows] = useState(false);

  const getPartnerLogos = async () => {
    const headers = {
      "X-Api-Version": "v3",
      "Accept-Language": i18n.language,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios.get(
        `${baseCMSURL}/business-partners?pagination[page]=1&pagination[pageSize]=10`,
        { headers }
      );

      let logos =
        response.data?.data?.length
          ? response.data.data.map((item: any) => ({
              id: item.id,
              imageUrl: item.imageUrl,
            }))
          : PartnerLogosList.map((logo, i) => ({ id: i, imageUrl: logo }));

      if (isRtl) logos = logos.slice().reverse();

      setPartnerLogos(logos);
      setImagesLoaded(0);
    } catch (error) {
      let logos = PartnerLogosList.map((logo, i) => ({ id: i, imageUrl: logo }));
      if (isRtl) logos = logos.slice().reverse();
      setPartnerLogos(logos);
      setImagesLoaded(0);
    }
  };

  useEffect(() => {
    getPartnerLogos();
  }, []);

  const checkOverflow = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (partnerLogos.length < 4) {
      setShowArrows(false);
      return;
    }

    const overflow = el.scrollWidth > el.clientWidth + 5;
    setShowArrows(overflow);
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    if (partnerLogos.length === 0) return;
    if (imagesLoaded < partnerLogos.length) return;
    const t = setTimeout(() => checkOverflow(), 50);
    return () => clearTimeout(t);
  }, [imagesLoaded, partnerLogos]);

  useEffect(() => {
    const handler = () => checkOverflow();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const scrollAmount = Math.max(clientWidth * 0.8, clientWidth / 2);

    if (direction === "right") {
      if (scrollLeft + clientWidth >= scrollWidth - 1) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollTo({
          left: Math.min(scrollLeft + scrollAmount, maxScroll),
          behavior: "smooth",
        });
      }
    } else {
      if (scrollLeft <= 1) {
        el.scrollTo({ left: maxScroll, behavior: "smooth" });
      } else {
        el.scrollTo({
          left: Math.max(scrollLeft - scrollAmount, 0),
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <div
      id={id}
      style={{
        transition: "opacity 0.5s ease, transform 0.5s ease",
        opacity: isTransitioning ? 0 : 1,
        transform: isTransitioning
          ? `translateX(${isTransitioning ? "-30px" : "30px"})`
          : "translateX(0)",
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            pt: 4,
            marginTop: "20px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Typography
            className={isRtl ? "rtl-text" : ""}
            variant="h5"
            gutterBottom
            align="center"
            sx={{
              ...textStyle,
              fontWeight: "bold",
              mb: isMobile ? 2 : 4,
              direction: isRtl ? "rtl" : "ltr",
            }}
          >
            {t("ourSuccessPartners")}
          </Typography>

          {showArrows && (
            <IconButton
              onClick={() => scroll("left")}
              aria-label="scroll-left"
              sx={{
                position: "absolute",
                top: isMobile ? "75%" : "65%",
                left: 0,
                transform: "translateY(-50%)",
                backgroundColor: "white",
                boxShadow: 2,
                zIndex: 2,
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}

          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              overflowX: partnerLogos.length < 4 ? "hidden" : "auto", 
              overflowY: "hidden",
              "&::-webkit-scrollbar": { display: "none" },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none",
              alignItems: "center",
              direction: "ltr",
              px: 1,
              justifyContent:
                partnerLogos.length < 4 ? "center" : "flex-start",
            }}
          >
            {partnerLogos.map(
              (
                logo: { id: any; imageUrl: string | undefined },
                index: Key | null | undefined
              ) => (
                <Box
                  key={index}
                  component="img"
                  src={logo?.imageUrl}
                  alt={`Partner ${logo?.id}`}
                  onLoad={() => setImagesLoaded((n) => n + 1)}
                  onError={() => setImagesLoaded((n) => n + 1)}
                  sx={{
                    objectFit: "contain",
                    width: isMobile ? "30%" : "17%",
                    mx: 2,
                  //  opacity: 0.5,
                    transform: "scale(1)",
                    transition: "opacity 0.3s ease, transform 0.3s ease",
                    "&:hover": {
                      opacity: 1,
                      transform: "scale(1.1)",
                      cursor: "pointer",
                    },
                  }}
                />
              )
            )}
          </Box>
          {showArrows && (
            <IconButton
              onClick={() => scroll("right")}
              aria-label="scroll-right"
              sx={{
                position: "absolute",
                top: isMobile ? "75%" : "65%",
                right: 0,
                transform: "translateY(-50%)",
                backgroundColor: "white",
                boxShadow: 2,
                zIndex: 2,
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default PartnerSectionComponent;
